<template>
  <div id="general-company-info" class="component">
    <form-status :v="$v" />
    <form submit.prevent novalidate method="post" enctype="multipart/form-data">
      <!-- purchase new engines -->
      <div class="form-group">
        <label for="purchaseNewEngine" class="control-label required">
          Are you a prospective customer looking to purchase new (non-used) engines?
        </label>
        <div class="renewal-radio-btns">          
          <div class="radio-btn">
            <input
              id="purchaseNewEngineYes"
              v-model="company.purchaseNewEngine"
              type="radio"
              :value="true"
            />
            <label for="purchaseNewEngineYes">Yes</label>
          </div>
          <div class="radio-btn">
            <input id="purchaseNewEngineNo" v-model="company.purchaseNewEngine" type="radio" :value="false"/>
            <label for="purchaseNewEngineNo">No</label>
          </div>
        </div>
        <div v-if="!$v.company.purchaseNewEngine.required && $v.company.purchaseNewEngine.$error" class="text-danger row-padding-left">
            Please select Are you a prospective customer looking to purchase new (non-used) engines?
        </div>
      </div>

      <!-- renewal field -->
      <div class="form-group" v-if="!company.purchaseNewEngine">
        <label class="control-label required">Is this request a renewal of a previous licensing agreement?</label>
        <div class="renewal-radio-btns mb-3">
          <div class="radio-btn">
            <input
              id="renewalYes"
              v-model="company.commercialLicenseRenewal"
              type="radio"
              :value="true"
              :disabled="disableRenewalYes"
            />
            <label for="renewalYes">Yes</label>
          </div>
          <div class="radio-btn">
            <input id="renewalNo" v-model="company.commercialLicenseRenewal" type="radio" :value="false" />
            <label for="renewalNo">No</label>
          </div>
        </div>
        <!-- current renewal number -->
        <div v-if="company.commercialLicenseRenewal" class="form-group">
          <label for="currentAgreementNumber" class="form-label required">Current Agreement Number</label>
          <input
            id="currentAgreementNumber"
            v-model="company.currentAgreementNumber"
            class="form-control"
            type="text"
            @blur="$v.company.currentAgreementNumber.$touch();"
          />
          <div
            v-if="!$v.company.currentAgreementNumber.required && $v.company.currentAgreementNumber.$error"
            class="text-danger"
          >
            Please enter current agreement number
          </div>
        </div>
      </div>

      <div class="form-group">
        <label class="control-label required"
          >Which of the following products do you use/manage or are you interested in?
          <tooltip id="product-type-info" :content="productDescription" effect="scale" placement="right"
            ><i class="fa fa-question-circle"></i>
          </tooltip>
        </label>
        <div id="product-type" class="row">
          <div class="col-md-6">
            <div>
              <tooltip :content="tooltip.commercial" effect="scale" placement="right">
                <label class="checkbox-inline">
                  <input
                    v-model="commercialProduct"
                    :disabled="nonMilitaryProduct"
                    type="checkbox"
                    value="commercial"
                    @blur="$v.commercialProduct.$touch();"
                  />Commercial Engines
                </label>
              </tooltip>
            </div>
            <div class="productType">
              <tooltip :content="tooltip.military" effect="scale" placement="right">
                <label class="checkbox-inline">
                  <input
                    v-model="militaryProduct"
                    :disabled="disableMilitary || company.commercialLicenseRenewal"
                    type="checkbox"
                    value="military"
                  /><span>Military Engines</span>
                </label>
              </tooltip>
            </div>
          </div>
          <div class="col-md-6">
            <div class="productType">
              <tooltip :content="tooltip.airframe" effect="scale" placement="right">
                <label class="checkbox-inline">
                  <input
                    v-model="airframeProduct"
                    :disabled="nonMilitaryProduct || company.commercialLicenseRenewal"
                    type="checkbox"
                    value="airframe"
                    @blur="$v.commercialProduct.$touch();"
                  /><span>Airframe Systems</span>
                </label>
              </tooltip>
            </div>
            <div class="productType">
              <tooltip :content="tooltip.bga" effect="scale" placement="right">
                <label class="checkbox-inline">
                  <input
                    v-model="bgaProduct"
                    :disabled="nonMilitaryProduct || company.commercialLicenseRenewal"
                    type="checkbox"
                    value="bga"
                    @blur="$v.commercialProduct.$touch();"
                  /><span>Business & General Aviation Engines (BGA)</span>
                </label>
              </tooltip>
            </div>
          </div>
          <div v-if="nonMilitaryProduct" id="military-warning-message" class="col-md-12">
            {{ militaryProductWarning }}
          </div>
          <div v-if="$v.commercialProduct.$error && !militaryProductWarning" class="col-md-12 text-danger">
            Please select at least one product
          </div>
        </div>
      </div>
      <div v-show="!nonMilitaryProduct" class="row">
        <div v-if="!disableMilitary" class="form-group col-md-12">
          <label class="control-label required"
            >Company Type
            <tooltip :content="tooltip.company" effect="scale" placement="right"
              ><i class="fa fa-question-circle"></i
            ></tooltip>
          </label>
          <select class="form-control" disabled>
            <option value="" selected>Select Company Type</option>
          </select>
        </div>
        <!-- select Commercial Engines - Description of Entity -->
        <div v-if="commercialProduct" class="form-group col-md-12">
          <label class="control-label required">
            Commercial Engines - Description of Entity
            <!-- <tooltip :content="tooltip.company" effect="scale" placement="right"
              ><i class="fa fa-question-circle"></i></tooltip> -->
          </label>
          <div class="row row-padding-left">
            <div v-for="(company,index) in commercialCompanies" v-bind:key="company.companyTypeId" class="radio-padding-bottom col-lg-3 col-md-4 col-sm-6 col-xs-12">
              <span v-if="tooltipCommercial(company.companyTypeId)">
                <tooltip :content="tooltipCommercial(company.companyTypeId)" effect="scale" :placement="tooltipPlacement(index)" class="product-tooltip">
                <label class="radio-inline">
                  <input :id = company.companyTypeId type="radio" v-model="commercialCompanyType" :name="company.companyTypeName" 
                  :value="company.companyTypeId" @blur="$v.commercialCompanyType.$touch();">
                  <span>{{company.companyTypeName}}</span>
                </label>
                </tooltip>
              </span>
              <span v-else>
                <label class="radio-inline">
                  <input :id = company.companyTypeId type="radio" v-model="commercialCompanyType" :name="company.companyTypeName" 
                  :value="company.companyTypeId" @blur="$v.commercialCompanyType.$touch();">
                  <span>{{company.companyTypeName}}</span>
                </label>
              </span>                                    
            </div>
          </div>
          <div v-if="!$v.commercialCompanyType.required && $v.commercialCompanyType.$error" class="text-danger row-padding-left">
            Please select Commercial Engines - Description of Entity
          </div>
        </div>
        <!-- select FAA 145 Approved MRO or Equivalent -->
        <div v-if="showMROType" class="row mro-row-margin">
          <div class="form-group col-lg-3 col-md-12 col-sm-12 mro-padding-right">
            <label class="control-label required">FAA 145 Approved MRO or Equivalent</label>
            <select v-model="commercialCompanyOther" class="form-control" @blur="$v.commercialCompanyOther.$touch();">
              <option value="" selected>Select MRO Type</option>
              <option v-for="mro in MROtype" :key="mro" :value="mro">
                {{ mro }}
              </option>
            </select>
            <div v-if="!$v.commercialCompanyOther.required && $v.commercialCompanyOther.$error" class="text-danger">
              Please select FAA 145 Approved MRO or Equivalent
            </div>
            <div v-if="agreedOverhaulTNC">
              <label class="checkbox-inline lbloverhaulTNC">
                <input
                  type="checkbox"
                  disabled = true
                  checked = true
                />
                <span>Overhaul Shop Acknowledgement compliance</span>
              </label>
            </div>
          </div>
          <!-- select MRO Needed Benefits -->
          <div v-if="showMRONeededBenefits" class="form-group col-lg-9 col-md-12 col-sm-12 mro-benefits-padding">
            <div class="col-md-12 col-sm-12">
              <label class="control-label">Select Needed Benefits:</label>
              <a @click="showMRONeededBenefitsModal=true" class="mro-benefits-link">View Comparison Chart</a>
            </div>
            <div v-for="mro in mroNeededBenefitsList" v-bind:key="mro" class="col-lg-4 col-md-4 col-sm-6">
              <label class="radio-inline">
                <input :id = mro type="radio" v-model="mroNeededBenefits" :name="mro" 
                :value="mro" @blur="$v.mroNeededBenefits.$touch();">
                <span>{{mro}}</span>
              </label>                  
            </div>
            <div v-if="!$v.mroNeededBenefits.required && $v.mroNeededBenefits.$error" class="text-danger col-md-12 col-sm-12">
              Please select Needed Benefits
            </div>
          </div>         
        </div>
        <!-- select airframe comapny -->
        <div v-if="airframeProduct" class="form-group col-md-12">
          <label class="control-label required">
            Airframe Systems Company Type
            <!-- <tooltip :content="tooltip.company" effect="scale" placement="right"
              ><i class="fa fa-question-circle"></i> </tooltip> -->
          </label>
          <div class="row row-padding-left">
            <div v-for="company in airframe.companyType" v-bind:key="company.companyTypeId" class="col-lg-3 col-md-4 col-sm-6 col-xs-12">
              <label class="radio-inline">
                <input :id = "'airframe-'+company.companyTypeId" type="radio" v-model="airframeCompanyType" :name="'airframe-'+company.companyTypeName" 
                :value="company.companyTypeId" @blur="$v.airframeCompanyType.$touch();">
                <span>{{company.companyTypeName}}</span>
              </label>                    
            </div>
          </div>
          <div v-if="!$v.airframeCompanyType.required && $v.airframeCompanyType.$error" class="text-danger row-padding-left">
            Please select airframe systems company type
          </div>
        </div>
        <!-- airframe other company -->
        <div v-if="airframeCompanyType == '6'" class="form-group col-md-12">
          <label for="" class="control-label required">Please describe the support you are requesting</label>
          <input
            v-model.trim="airframeCompanyOther"
            type="text"
            class="form-control"
            @blur="$v.airframeCompanyOther.$touch();"
          />
          <div v-if="!$v.airframeCompanyOther.required && $v.airframeCompanyOther.$error" class="text-danger">
            This field cannot be blank
          </div>
          <div v-if="!$v.airframeCompanyOther.nameValidator && $v.airframeCompanyOther.$error" class="text-danger">
            Only alphanumeric and ' . , ( ) & _ - are allowed
          </div>
          <div v-if="!$v.airframeCompanyOther.maxLength && $v.airframeCompanyOther.$error" class="text-danger">
            Allowed upto 100 characters only
          </div>
        </div>
        <!-- select BGA company -->
        <div v-if="bgaProduct" class="form-group col-md-12">
          <label class="control-label required">
            BGA Company Type
            <!-- <tooltip :content="tooltip.company" effect="scale" placement="right"
              ><i class="fa fa-question-circle"></i></tooltip> -->
          </label>
          <div class="row row-padding-left">
            <div v-for="company in bga.companyType" v-bind:key="company.companyTypeId" class="col-lg-3 col-md-4 col-sm-6 col-xs-12">
              <label class="radio-inline">
                <input :id = "'bga-'+company.companyTypeId" type="radio" v-model="bgaCompanyType" :name="'bga-'+company.companyTypeName" 
                :value="company.companyTypeId" @blur="$v.bgaCompanyType.$touch();">
              <span>{{company.companyTypeName}}</span>        
              </label>               
            </div>
          </div>
          <div v-if="!$v.bgaCompanyType.required && $v.bgaCompanyType.$error" class="text-danger row-padding-left">
            Please select BGA company type
          </div>
        </div>
        <!-- bga other company -->
        <div v-if="bgaCompanyType == '6'" class="form-group col-md-12">
          <label for="" class="control-label required">Please describe the support you are requesting</label>
          <input v-model.trim="bgaCompanyOther" type="text" class="form-control" @blur="$v.bgaCompanyOther.$touch();" />
          <div v-if="!$v.bgaCompanyOther.required && $v.bgaCompanyOther.$error" class="text-danger">
            This field cannot be blank
          </div>
          <div v-if="!$v.bgaCompanyOther.nameValidator && $v.bgaCompanyOther.$error" class="text-danger">
            Only alphanumeric and ' . , ( ) & _ - are allowed
          </div>
          <div v-if="!$v.bgaCompanyOther.maxLength && $v.bgaCompanyOther.$error" class="text-danger">
            Allowed upto 100 characters only
          </div>
        </div>
        <!-- Company name changed? -->
        <div v-if="company.commercialLicenseRenewal" class="form-group col-md-12">
          <label class="control-label required">Has your company name changed?</label>
          <div class="renewal-radio-btns mb-3">
            <div class="radio-btn">
              <input
                id="companyNameChangedYes"
                v-model="company.companyNameChanged"
                type="radio"
                :value="true"
              />
              <label for="companyNameChangedYes">Yes</label>
            </div>
            <div class="radio-btn">
              <input id="companyNameChangedNo" v-model="company.companyNameChanged" type="radio" :value="false" />
              <label for="companyNameChangedNo">No</label>
            </div>
          </div>
        </div>
        <div v-if = "!(company.commercialLicenseRenewal && !company.companyNameChanged)">
          <!-- select country -->
          <div class="form-group col-md-12">
            <label for="" class="control-label required">Country or State of Incorporation/Creation</label>
            <select
              id="country"
              v-model="company.companyCountry"
              class="form-control"
              @blur="$v.company.companyCountry.$touch();"
            >
              <option value="">Select Country</option>
              <option v-for="country in countries" :key="country.cntryId" :value="country.cntryId">
                {{ country.cntryNm }}
              </option>
            </select>
            <div v-if="!$v.company.companyCountry.required && $v.company.companyCountry.$error" class="text-danger">
              Please select country
            </div>
          </div>
          <!-- company legal name -->
          <div class="form-group col-md-12">
            <label for="" class="control-label required">Company Legal Name</label>
            <tooltip
              trigger="focus"
              effect="scale"
              placement="bottom"
              content="Exact company name per Dun & Bradstreet must be listed."
            >
              <input
                v-model.trim="company.companyName"
                type="text"
                class="form-control"
                autocomplete="no"
                @blur="$v.company.companyName.$touch();"
              />
            </tooltip>
            <div v-if="!$v.company.companyName.required && $v.company.companyName.$error" class="text-danger">
              Please enter company legal name
            </div>
            <div v-if="!$v.company.companyName.nameValidator && $v.company.companyName.$error" class="text-danger">
              Only alphanumeric and ' . , ( ) & _ - are allowed
            </div>
            <div v-if="!$v.company.companyName.maxLength && $v.company.companyName.$error" class="text-danger">
              Allowed upto 100 characters only
            </div>
          </div>
          <!-- DUNS Number -->
          <div v-if="showDUNSNumber">
            <div class="col-md-12 padding-left-0">
              <div div class="col-md-3">
                <label for="" class="required">Dun & Bradstreet (D-U-N-S) Number</label>
              </div>
              <div div class="col-md-9">
              </div>
            </div>
            <div class="col-md-12 padding-left-0">
              <div div class="col-md-3">
                <input
                  v-model.trim="company.dunsNumber"
                  type="text"
                  class="form-control"                  
                  :disabled = !company.dunsNumberAvailable
                  @blur="$v.company.dunsNumber.$touch();"
                />
              </div>
              <div class="col-md-9">
                <div :class="{'form-group': !company.dunsNumberAvailable}">
                  <label class="checkbox-inline text-top">
                    <input
                      v-model="company.dunsNumberAvailable"
                      :true-value = false
                      :false-value = true
                      type="checkbox"
                    />
                    <span>I do not have a DUNS Number</span>
                  </label>
                </div>
                <div v-if="!company.dunsNumberAvailable">
                  <label class="required" for="">
                  Please provide a valid document to verify your name and address.
                  </label>
                  <p class="margin-0">Acceptable documents include:</p>
                  <p class="margin-0">1. Business Registration Certificate</p>
                  <p class="margin-0">2. Articles of Incorporation (fully executed)</p>
                  <p class="margin-0">3. Business formation documents approved by the respective government agency in the country where the entity is formed.</p>
                  <p class="margin-0">4. Business Card</p>
                  <p class="margin-0">5. Letterhead (PDF)</p>
                  <file-upload
                    upload-section="generalInfo"
                    document-type="CompanyVerificationDocument"
                    :document-name="companyVerificationDocument"
                    :document-status="getDocumentStatus('CompanyVerificationDocument')"
                    :v="$v.companyVerificationDocument"
                  />
                </div>
              </div>
            </div>
            <div class="form-group col-md-12">
              <div
                v-if="!$v.company.dunsNumber.required && $v.company.dunsNumber.$error"
                class="text-danger"
              >
                Please enter Dun & Bradstreet (D-U-N-S) Number or select I do not have a DUNS Number
              </div>
              <div
                v-if="!$v.company.dunsNumber.maxLength && $v.company.dunsNumber.$error"
                class="text-danger"
              >
                Allowed upto 100 characters only
              </div>
            </div>
          </div>
          <!-- company's website link -->
          <div class="form-group col-md-12">
            <label for="" class="control-label required">Please provide a link to your company's website</label>
            <input
              v-model.trim="company.companyWebsiteLink"
              type="text"
              class="form-control"
              autocomplete="no"
              @blur="$v.company.companyWebsiteLink.$touch();"
            />
            <div v-if="!$v.company.companyWebsiteLink.required && $v.company.companyWebsiteLink.$error" class="text-danger">
              Please provide a link to your company's website
            </div>
            <div v-if="!$v.company.companyWebsiteLink.maxLength && $v.company.companyWebsiteLink.$error" class="text-danger">
              Allowed upto 200 characters only
            </div>
          </div>
          <div class="form-group col-md-12">
            <label for="" class="control-label"
              >Alias name/Doing Business As (DBA)/Commercial Name</label
            >
            <input
              v-model="company.companyAnotherName"
              type="text"
              class="form-control"
              @blur="$v.company.companyAnotherName.$touch();"
            />
            <div
              v-if="!$v.company.companyAnotherName.maxLength && $v.company.companyAnotherName.$error"
              class="text-danger"
            >
              Allowed upto 100 characters only.
            </div>
            <div
              v-if="!$v.company.companyAnotherName.nameValidator && $v.company.companyAnotherName.$error"
              class="text-danger"
            >
              Only alphanumeric and ' . , ( ) & _ - are allowed
            </div>
          </div>
          <!-- Has your company legal name changed? -->
          <div v-if = "!company.commercialLicenseRenewal" class="form-group col-md-12">
            <label for="legal-name" class="control-label">Has your company legal name changed?</label>
            <select id="legal-name" v-model="company.isLegalNameChange" name="" class="form-control">
              <option value="No">No</option>
              <option value="Yes">Yes</option>
            </select>
          </div>
          <!-- show if isLegalNameChange=Yes -->
          <div v-if="!company.commercialLicenseRenewal && company.isLegalNameChange == 'Yes'">
            <div class="form-group col-md-4">
              <label for="" class="required">Previous Company Legal Name</label>
              <tooltip
                trigger="focus"
                effect="scale"
                placement="bottom"
                content="Exact company name per Dun & Bradstreet must be listed."
              >
                <input
                  v-model.trim="company.prevCompanyLegalName"
                  type="text"
                  class="form-control"
                  autocomplete="no"
                  @blur="$v.company.prevCompanyLegalName.$touch();"
                />
              </tooltip>
              <div
                v-if="!$v.company.prevCompanyLegalName.required && $v.company.prevCompanyLegalName.$error"
                class="text-danger"
              >
                Please enter previous company legal Name
              </div>
              <div
                v-if="!$v.company.prevCompanyLegalName.nameValidator && $v.company.prevCompanyLegalName.$error"
                class="text-danger"
              >
                Only alphanumeric and ' . , ( ) & _ - are allowed
              </div>
              <div
                v-if="!$v.company.prevCompanyLegalName.maxLength && $v.company.prevCompanyLegalName.$error"
                class="text-danger"
              >
                Allowed upto 100 characters only
              </div>
            </div>
            <div class="form-group col-md-4">
              <label for="" class="required">Date Of Name Change </label>
              <div>
                <datepicker
                  id="nameChange"
                  v-model.trim="company.nameChangeDate"
                  placeholder="(MM/DD/YYYY)"
                  :disabled-dates="disabledDates"
                  :bootstrap-styling="true"
                  format="MM/dd/yyyy"
                  @blur="$v.company.nameChangeDate.$touch();"
                ></datepicker>
              </div>
              <div v-if="!$v.company.nameChangeDate.required && $v.company.nameChangeDate.$error" class="text-danger">
                Please enter date of name change
              </div>
            </div>
          </div>
            <div v-if = "(company.commercialLicenseRenewal && company.companyNameChanged)||(!company.commercialLicenseRenewal && company.isLegalNameChange == 'Yes')" class="form-group col-md-4">
              <label for="file" class="required">Attach documentation supporting the change of company name</label>
              <!-- display uploaded file -->
              <file-upload
                upload-section="generalInfo"
                document-type="LegalNameChangeDocument"
                :document-name="legalNameChangeDocument"
                :document-status="getDocumentStatus('LegalNameChangeDocument')"
                :v="$v.legalNameChangeDocument"
              />
            </div>
          <div class="form-group col-md-12">
            <label for="" class="control-label">Parent Company, if applicable</label>
            <input
              v-model.trim="company.parentCompany"
              type="text"
              class="form-control"
              @blur="$v.company.parentCompany.$touch();"
            />
            <div v-if="!$v.company.parentCompany.nameValidator && $v.company.parentCompany.$error" class="text-danger">
              Only alphanumeric and ' . , ( ) & _ - are allowed
            </div>
            <div v-if="!$v.company.parentCompany.maxLength && $v.company.parentCompany.$error" class="text-danger">
              Allowed upto 100 characters only
            </div>
          </div>
          <div>            
            <div class="form-group col-md-4">
              <label for="" :class="['control-label', {required: company.purchaseNewEngine}]">CFM Contact Name</label>
              <input
                v-model.trim="company.supportGEContact"
                type="text"
                class="form-control"
                @blur="$v.company.supportGEContact.$touch();"
              />
              <div v-if="!$v.company.supportGEContact.required && $v.company.supportGEContact.$error" class="text-danger">
              Please enter CFM contact name
            </div>
            <div v-if="!$v.company.supportGEContact.maxLength && $v.company.supportGEContact.$error" class="text-danger">
                Allowed upto 100 characters only
              </div>
            </div>
            <div class="form-group col-md-4">
              <label for="contactEmail" :class="['control-label', {required: company.purchaseNewEngine}]">CFM Contact Email</label>
              <input
                id="contactEmail"
                v-model.trim="company.contactEmail"
                type="email"
                class="form-control"
                @blur="$v.company.contactEmail.$touch();"
              />
              <div v-if="!$v.company.contactEmail.required && $v.company.contactEmail.$error" class="text-danger">
              Please enter CFM contact email
            </div>
            <div v-if="!$v.company.contactEmail.email && $v.company.contactEmail.$error" class="text-danger">
                Please enter valid email address
              </div>
              <div v-if="!$v.company.contactEmail.maxLength && $v.company.contactEmail.$error" class="text-danger">
                Allowed upto 150 characters only
              </div>
            </div>
            <div class="form-group col-md-4">
              <label for="" :class="['control-label', {required: company.purchaseNewEngine}]">CFM Contact Phone Number</label>
              <input
                v-model.trim="company.contactPhoneNumber"
                type="text"
                class="form-control"
                @blur="$v.company.contactPhoneNumber.$touch();"
              />
            <div v-if="!$v.company.contactPhoneNumber.required && $v.company.contactPhoneNumber.$error" class="text-danger">
              Please enter CFM contact phone number
            </div>
              <div v-if="!$v.company.contactPhoneNumber.maxLength && $v.company.contactPhoneNumber.$error" class="text-danger">
                Allowed upto 15 characters only
              </div>
            </div>
          </div>
          <!-- Invoice Receipt -->
          <div v-if="showInvoiceReceipt" class="form-group col-md-12">
            <label for="" class="control-label required">Please select how you would like to receive invoices?</label>
            <div v-for="(list, index) in invoiceReceiptList" v-bind:key="list"
                :class="['checkbox', {'mt-0': index === 0}, {'mb-0': index === invoiceReceiptList.length-1}]">
              <label>
                <input
                  :id = list
                  :name="list"
                  :value="list"
                  v-model="company.invoiceReceipt"
                  type="checkbox"
                  @blur="$v.company.invoiceReceipt.$touch();"     
                />{{list}}
              </label>                                   
            </div>
            <div v-if="!$v.company.invoiceReceipt.required && $v.company.invoiceReceipt.$error" class="text-danger">
              Please select how you would like to receive invoices
            </div>
          </div>
          <!-- Purchase Parts -->
          <div v-if="showPurchaseParts" class="form-group col-md-12">
            <label for="" class="control-label required">Do you plan to purchase parts from GE?</label>
            <div class="renewal-radio-btns">          
              <div class="radio-btn">
                <input
                  id="purchasePartsYes"
                  v-model="company.purchaseParts"
                  type="radio"
                  value="Yes"
                />
                <label for="purchasePartsYes">Yes</label>
              </div>
              <div class="radio-btn">
                <input id="purchasePartsNo" v-model="company.purchaseParts" type="radio" value="No"/>
                <label for="purchasePartsNo">No</label>
              </div>
            </div>
            <div v-if="!$v.company.purchaseParts.required && $v.company.purchaseParts.$error" class="text-danger">
              Please select do you plan to purchase parts from GE
            </div>
          </div>
          <!-- Purchasing Information -->
          <div v-if="showPurchaseParts && company.purchaseParts === 'Yes'">
            <!-- Purchase Type of Parts -->
            <div class="form-group col-md-12">
              <label for="" class="control-label required">What type of Parts do you plan to purchase?</label>
              <div v-for="(list, index) in purchaseTypeofPartsList" v-bind:key="list" 
                    :class="['checkbox', {'mt-0': index === 0}, {'mb-0': index === purchaseTypeofPartsList.length-1}]">
                <label>
                  <input
                    :id = list
                    :name="list"
                    :value="list"
                    v-model="company.purchaseTypeofParts"
                    type="checkbox"
                    @blur="$v.company.purchaseTypeofParts.$touch();"     
                  />{{list}}
                </label>                                   
              </div>
              <div v-if="!$v.company.purchaseTypeofParts.required && $v.company.purchaseTypeofParts.$error" class="text-danger">
                Please select what type of parts do you plan to purchase
              </div>
            </div>
            <!-- Purchasing Contact Name -->
            <div class="form-group col-md-12">
              <label for="purchasingContactName" class="control-label required">Purchasing Contact Name</label>
              <input
                id="purchasingContactName"
                v-model.trim="company.purchasingContactName"
                type="text"
                class="form-control"
                @blur="$v.company.purchasingContactName.$touch();"
              />
              <div v-if="!$v.company.purchasingContactName.required && $v.company.purchasingContactName.$error" class="text-danger">
              Please enter Purchasing Contact Name
            </div>
            <div v-if="!$v.company.purchasingContactName.maxLength && $v.company.purchasingContactName.$error" class="text-danger">
                Allowed upto 100 characters only
              </div>
            </div>
            <!-- Purchasing Contact Email -->
            <div class="form-group col-md-12">
              <label for="purchasingContactEmail" class="control-label required">Purchasing Contact Email</label>
              <input
                id="purchasingContactEmail"
                v-model.trim="company.purchasingContactEmail"
                type="email"
                class="form-control"
                @blur="$v.company.purchasingContactEmail.$touch();"
              />
              <div v-if="!$v.company.purchasingContactEmail.required && $v.company.purchasingContactEmail.$error" class="text-danger">
                Please enter Purchasing Contact Email
              </div>
              <div v-if="!$v.company.purchasingContactEmail.email && $v.company.purchasingContactEmail.$error" class="text-danger">
                Please enter valid email address
              </div>
              <div v-if="!$v.company.purchasingContactEmail.maxLength && $v.company.purchasingContactEmail.$error" class="text-danger">
                Allowed upto 150 characters only
              </div>
            </div>
            <!-- Purchase Submit Order -->
            <div class="form-group col-md-12">
              <label for="" class="control-label required">How are you planning to submit orders?</label>
              <div class="renewal-radio-btns">          
                <div class="radio-btn">
                  <input
                    id="purchaseSubmitOrderGE"
                    v-model="company.purchaseSubmitOrder"
                    type="radio"
                    value="GE Website"
                  />
                  <label for="purchaseSubmitOrderGE">GE Website</label>
                </div>
                <div class="radio-btn">
                  <input id="purchaseSubmitOrderSpec" v-model="company.purchaseSubmitOrder" type="radio" value="Spec 2000"/>
                  <label for="purchaseSubmitOrderSpec">Spec 2000</label>
                </div>
              </div>
              <div v-if="!$v.company.purchaseSubmitOrder.required && $v.company.purchaseSubmitOrder.$error" class="text-danger">
                Please select how are you planning to submit orders
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="action-btns">
        <input v-if="nonMilitaryProduct" type="button" class="btn btn-sm btn-primary" value="Back" @click="back" />
        <input v-else type="submit" class="btn btn-sm btn-primary" value="Next" @click="gotoNextSection" />
        <strong v-if="formErrorMsg" class="text-danger">Please fill all required fields to proceed further.</strong>
      </div>
    </form>
    <!-- Modal to display MRO Needed Benefits Chart -->
    <MRONeededBenefitsChart v-model="showMRONeededBenefitsModal"/>
    <!-- Modal to show TNC for Full Engine Overhaul - 145 Approved MRO -->
    <modal v-model="showOverhaulTNCModal" effect="fade" :backdrop="false">
      <div slot="modal-header" class="modal-header">
        <span class="modal-title" style="color: black;">Overhaul Shop</span>
        <img @click="overhaulTNCModalAction('Cancel')" class="pull-right" src="./../../../mygea/images/close.png" alt="Close Modal" style="cursor: pointer;"/>
      </div>
      <div slot="modal-body" class="modal-body" style="font-size: 15px;">
        "Overhaul Shop" refers to a provider of overhaul services certified by the FAA, EASA, or any other airworthiness authority.
         Overhaul refers to the following tasks, either performed by the Overhaul Shop directly or outsourced to a third party:
          (i) disassembly of an engine into piece parts; (ii) cleaning piece parts for inspection; (iii) inspecting piece parts for
           comparison to their respective serviceability limits; (iv) coordinating with the airline to define the work scope and desired
            goals for the overhaul; (v) managing material logistics; (vi) installing serviceable parts; (vii) reassembly of the engine;
             (viii) verifying engine performance via an engine test; and (ix) carrying out Fully Disclosed Repairs as part of an engine
              overhaul.
      </div>
      <div slot="modal-footer" class="modal-footer">
        <button type="button" class="btn btn-sm btn-secondary" @click="overhaulTNCModalAction('Cancel')" >Cancel</button>
        <button type="button" class="btn btn-sm btn-info" @click="overhaulTNCModalAction('Confirm')" >Confirm</button>
      </div>
    </modal>
  </div>
</template>
<script>
import tooltip from "build-ui-x/src/Tooltip";
import Datepicker from "vuejs-datepicker";
import { mapState } from "vuex";
import { required, requiredIf, maxLength, email } from "vuelidate/lib/validators";
import { nameValidator } from "@/common/vuelidate/customValidators";
import { Company } from "@/model/Company";
import { Product } from "@/model/Product";
import { ProductInfo } from "@/model/ProductInfo";
import { Mro } from "@/model/Mro";
import fileUpload from "@/mycfm/components/new-company/FileUpload";
import formStatus from "@/mycfm/components/new-company/FormStatus";
import moment from "moment";
import MRONeededBenefitsChart from "@/mycfm/components/new-company/MRONeededBenefitsChart";
import modal from "build-ui-x/src/Modal";

const requiredProduct = function() {
  return this.commercialProduct || this.airframeProduct || this.bgaProduct;
};
export default {
  components: {
    tooltip,
    fileUpload,
    formStatus,
    Datepicker,
    MRONeededBenefitsChart,
    modal
  },
  data() {
    return {
      militaryProduct: false,
      tooltip: {
        commercial:
          "Engine models used in revenue service by commercial airlines (FAA Part 121 operations for example)",
        airframe:
          "Integrated aircraft systems from GE Aerospace that include Avionics, Electrical Power, Structures, Dowty propellers, and Unison. Examples include avionics, actuation and landing gear, aerostructures, electrical power systems, propeller systems, etc.)",
        military:
          "Engine models installed in military aircraft. Does not include commercial engine models powering aircraft operated by a government or military.",
        bga:
          "These engines are used in business jet or general aviation operations (FAA part 91 or part 135 operations for example)",
        company:
          "Please select the option that best describes your company's operations related to the product type selected above."
      },
      formErrorMsg: false,
      disabledDates: {
        from: new Date()
      },
      MROtype: ["Line Maintenance", "Full Engine Overhaul", "Engine Piece Part or Engine Component Repair"],
      showMRONeededBenefitsModal: false,
      showOverhaulTNCModal: false,
      agreedOverhaulTNC : false,
      oldPurchaseNewEngineValue : null,
      invoiceReceiptList: [
        "Receive Paper Invoice via postal mail",
        "Receive Electronic Invoice via email",
        "Receive Electronic Invoicing via Electronic Data Interchange/Spec2000"
      ],
      purchaseTypeofPartsList: [
        "New Engine",
        "Spares Parts",
        "Component Repairs",
        "Used Materials"
      ]
    };
  },
  computed: {
    ...mapState("country", ["countries"]),
    ...mapState("company", ["company"]),
    ...mapState("product", ["product"]),
    ...mapState("mro", ["productMRO"]),
    ...mapState("productInfo", ["commercial", "airframe", "bga"]),
    militaryProductWarning() {
      let msg = "";
      if (this.militaryProduct) {
        msg = "For Military, Please contact your GE representative.";
      }
      return msg;
    },
    disableMilitary() {
      return this.commercialProduct || this.airframeProduct || this.bgaProduct;
    },
    nonMilitaryProduct() {
      return this.militaryProduct;
    },
    productDescription() {
      return `
        <strong>Commercial Engines:</strong>
        <p>${this.tooltip.commercial}</p>
        <strong>Airframe Systems:</strong>
        <p>${this.tooltip.airframe}</p>
        <strong>Business & General Aviation Engines:</strong>
        <p>${this.tooltip.bga}</p>
        <strong>Military Engines:</strong>
        <p>${this.tooltip.military}</p>`;
    },
    companyDescription() {
      return `
        <strong>${this.tooltip.company}</strong><br><br>
        <strong>Airline:</strong>
        <p>Operator of scheduled revenue airline service</p>
        <strong>Lessor:</strong>
        <p>Owner of aircraft leased to an operator</p>
        <strong>Line Maintenance:</strong>
        <p>Provider of line or base maintenance products and services</p>
        <strong>Engine Piece Part or Engine Component Repair:</strong>
        <p>Provider of repairs for engine parts</p>
        <strong>Full Engine Overhaul:</strong>
        <p>Overhaul facility able to approve an overhauled engine for return to service</p>`;
    },
    airframeProduct: {
      get() {
        return this.$store.state.product.productTypes.airframe !== null;
      },
      set(newVal) {
        if (newVal) {
          this.addNewProduct("airframe");
          this.$emit("addnewproduct");
        } else {
          let answer = window.confirm("You are going to lose the data. Are you sure to continue with this?");
          if (answer) {
            this.$router.go();
          } else {
            this.airframeProduct = true;
          }
        }
      }
    },
    airframeCompanyType: {
      get() {
        return (
          this.$store.state.product.productTypes.airframe &&
          this.$store.state.product.productTypes.airframe.companyDetails.companyTypeId
        );
      },
      set(value) {
        this.changeCompanyType("airframe", value);
        this.$emit("changecompany");
      }
    },
    airframeCompanyOther: {
      get() {
        return (
          this.$store.state.product.productTypes.airframe &&
          this.$store.state.product.productTypes.airframe.companyDetails.companyOtherName
        );
      },
      set(value) {
        this.updateCompanyOther("airframe", value);
      }
    },
    bgaProduct: {
      get() {
        return this.$store.state.product.productTypes.bga !== null;
      },
      set(newVal) {
        if (newVal) {
          this.addNewProduct("bga");
          this.$emit("addnewproduct");
        } else {
          let answer = window.confirm("You are going to lose the data. Are you sure to continue with this?");
          if (answer) {
            this.$router.go();
          } else {
            this.bgaProduct = true;
          }
        }
      }
    },
    bgaCompanyType: {
      get() {
        return (
          this.$store.state.product.productTypes.bga &&
          this.$store.state.product.productTypes.bga.companyDetails.companyTypeId
        );
      },
      set(value) {
        this.changeCompanyType("bga", value);
        this.$emit("changecompany");
      }
    },
    bgaCompanyOther: {
      get() {
        return (
          this.$store.state.product.productTypes.bga &&
          this.$store.state.product.productTypes.bga.companyDetails.companyOtherName
        );
      },
      set(value) {
        this.updateCompanyOther("bga", value);
      }
    },
    commercialProduct: {
      get() {
        return this.$store.state.product.productTypes.commercial !== null;
      },
      set(newVal) {
        if (newVal) {
          this.addNewProduct("commercial");
          this.$emit("addnewproduct");
        } else {
          let answer = window.confirm("You are going to lose the data. Are you sure to continue with this?");
          if (answer) {
            this.$router.go();
          } else {
            this.commercialProduct = true;
          }
        }
      }
    },
    commercialCompanyOther: {
      get() {
        let val =
          this.$store.state.product.productTypes.commercial &&
          this.$store.state.product.productTypes.commercial.companyDetails.companyOtherName;
        if (val) return val;
        else return "";
      },
      set(value) {
        this.updateCompanyOther("commercial", value);
        this.changeCommercialCompanyOther();
        this.$emit("changecompany");
        this.setMRONeededBenefits(value);
        //show TNC Modal for Full Engine Overhaul
        this.showOverhaulTNCModal = value == "Full Engine Overhaul" ? true : false;
        this.agreedOverhaulTNC = false;
      }
    },
    commercialCompanyType: {
      get() {
        return (
          this.$store.state.product.productTypes.commercial &&
          this.$store.state.product.productTypes.commercial.companyDetails.companyTypeId
        );
      },
      set(value) {
        this.changeCompanyType("commercial", value);
        this.$emit("changecompany");
      }
    },
    showMROType() {
      return [18].includes(
        this.$store.state.product.productTypes.commercial &&
          parseInt(this.$store.state.product.productTypes.commercial.companyDetails.companyTypeId)
      );
    },
    legalNameChangeDocument() {
      let documentName = "";
      let storedDocs = this.$store.state.company.company.companyDocuments;
      if (storedDocs != null && storedDocs.length != 0) {
        for (let i = 0; i < storedDocs.length; i++) {
          if (storedDocs[i].documentType == "LegalNameChangeDocument") {
            documentName = storedDocs[i].documentName;
            break;
          }
        }
      }
      return documentName;
    },
    commercialCompanies() {
      if (this.company.commercialLicenseRenewal && this.commercial.companyType != null) {
        return this.commercial.companyType.filter(company => {
          // return only renewable comnpany types
          return ["11", "12", "13", "14", "15", "16", "17", "18", "19"].includes(company.companyTypeId);
        });
      } else {
        return this.commercial.companyType;
      }
    },
    disableRenewalYes() {
      return !this.company.commercialLicenseRenewal && (this.bgaProduct || this.airframeProduct);
    },    
    mroNeededBenefitsList() {
      if(this.commercialCompanyOther == "Line Maintenance") {
        return [
            "LMSA - Line Maintenance Service Agreement",
            "PLA-LM - Purchase License Agreement - Line Maintenance",
            "GSL-LM - General Service Level - Line Maintenance"
          ]
      }
      else if(this.commercialCompanyOther == "Full Engine Overhaul") {
        return ["PLA-OH - Purchase License Agreement - Overhaul", "GSL-OH - General Service Level - Overhaul"]
      }
      else if(this.commercialCompanyOther == "Engine Piece Part or Engine Component Repair") {
        return ["PLA-CR - Purchase License Agreement - Component Repair", "GSL-CR - General Service Level - Component Repair"]
      }
      else {
        return [];
      }
    },
    mroNeededBenefits: {
      get() {
        let val =
          this.$store.state.product.productTypes.commercial &&
          this.$store.state.product.productTypes.commercial.companyDetails.mroNeededBenefits;
        if (val) return val;
        else return "";
      },
      set(value) {
        this.updateMRONeededBenefits("commercial", value);
      }
    },
    showMRONeededBenefits() {
      return this.showMROType && this.commercialCompanyOther !== "";
    },
    //show DUNSNumber section for Commercial Engines -> Airline OR Lessor
    showDUNSNumber() {      
      return this.commercialProduct && (this.commercialCompanyType == "1" || this.commercialCompanyType == "3");
    },
    companyVerificationDocument() {
      let documentName = "";
      let storedDocs = this.$store.state.company.company.companyDocuments;
      if (storedDocs != null && storedDocs.length != 0) {
        for (let i = 0; i < storedDocs.length; i++) {
          if (storedDocs[i].documentType == "CompanyVerificationDocument") {
            documentName = storedDocs[i].documentName;
            break;
          }
        }
      }
      return documentName;
    },
    //show Invoice Receipt section for Commercial Engines -> Airline OR Lessor
    showInvoiceReceipt() {      
      return this.commercialProduct && (this.commercialCompanyType == "1" || this.commercialCompanyType == "3");
    },
    //show Purchase Parts/Information  section for Commercial Engines -> Airline OR Lessor
    showPurchaseParts() {      
      return this.commercialProduct && (this.commercialCompanyType == "1" || this.commercialCompanyType == "3");
    }
  },
  watch: {
    "company.isLegalNameChange": function(newVal) {
      if (newVal == "No") {
        this.$store.dispatch("company/clearLegalNameChange");
        this.$store.dispatch("files/removeFile", "LegalNameChangeDocument");
      } else {
        this.$v.company.nameChangeDate.$reset();
        this.$v.company.prevCompanyLegalName.$reset();
      }
    },
    "company.companyNameChanged": function(newVal) {
      if (!newVal) {
        this.$store.dispatch("company/clearCompanyNameChange");
        this.$store.dispatch("company/clearLegalNameChange");
        this.$store.dispatch("files/removeFile", "LegalNameChangeDocument");
      } else {
        this.$v.company.nameChangeDate.$reset();
        this.$v.company.prevCompanyLegalName.$reset();
        this.$v.company.companyCountry.$reset();
        this.$v.company.companyName.$reset();
        this.$v.company.companyWebsiteLink.$reset();
        this.$v.company.companyAnotherName.$reset();
        this.$v.company.isLegalNameChange.$reset();
        this.$v.company.parentCompany.$reset();
        this.$v.company.supportGEContact.$reset();
        this.$v.company.contactEmail.$reset();
        this.$v.company.contactPhoneNumber.$reset();
      }
    },
    "company.commercialLicenseRenewal": function(newVal) {
      if (newVal) {
        this.commercialProduct = newVal;
      }

      // clear airframe and bga product selection
      if (newVal && this.airframeProduct) {
        this.airframeProduct = false;
        return;
      }
      if (newVal && this.bgaProduct) {
        this.bgaProduct = false;
      }
      if (!this.company.commercialLicenseRenewal) {
        let answer = window.confirm("You are going to lose the data. Are you sure to continue with this?");
        if (answer) {
          this.$router.go();
        } else {
          this.company.commercialLicenseRenewal = true;
          // assign purchase new engine to old value if user cancel reset data popup
          if(this.company.purchaseNewEngine) {
            this.company.purchaseNewEngine = this.oldPurchaseNewEngineValue
          }
        }
      } else {
        this.$store.dispatch("company/clearLegalNameChange");
        this.company.isLegalNameChange = "No";
      }
    },
    "company.dunsNumberAvailable": function(newVal) {
      if (!newVal) {
        this.company.dunsNumber = "";
        this.$v.company.dunsNumber.$reset();
      } else {
        this.$store.dispatch("files/removeFile", "CompanyVerificationDocument");
        this.$store.dispatch("company/removeFileMetaData", { documentType: "CompanyVerificationDocument" });
      }
    },
    "company.purchaseNewEngine": function(newVal, oldVal) {
      this.oldPurchaseNewEngineValue = oldVal;
      // trigger event to close all other opened section
      this.$emit("addnewproduct");
      if (newVal) {
        this.company.commercialLicenseRenewal = false;
        //reset commercial product information data
        if(this.commercialCompanyType != null) {
          this.commercialCompanyType = this.commercialCompanyType;
        } 
      }
    },
    "company.purchaseParts": function(newVal) {
      if (newVal === 'No') {
        this.$store.dispatch("company/clearPurchasingInformation");
      } else {
        this.$v.company.purchaseTypeofParts.$reset();
        this.$v.company.purchasingContactName.$reset();
        this.$v.company.purchasingContactEmail.$reset();
        this.$v.company.purchaseSubmitOrder.$reset();
      }
    }
  },
  validations() {
    if (this.company.commercialLicenseRenewal && !this.company.companyNameChanged) {
      return {
        commercialProduct: {
          requiredProduct
        },
        airframeProduct: {
          requiredProduct
        },
        bgaProduct: {
          requiredProduct
        },
        company: {
          purchaseNewEngine: {
            required
          },
          currentAgreementNumber: {
            required: requiredIf(function() {
              return this.company.commercialLicenseRenewal;
            })
          }
        },
        commercialCompanyType: {
          required: requiredIf(function() {
            return this.commercialProduct;
          })
        },
        airframeCompanyType: {
          required: requiredIf(function() {
            return this.airframeProduct;
          })
        },
        bgaCompanyType: {
          required: requiredIf(function() {
            return this.bgaProduct;
          })
        },
        commercialCompanyOther: {
          required: requiredIf(function() {
            return [18].includes(parseInt(this.commercialCompanyType));
          })
        },
        airframeCompanyOther: {
          nameValidator,
          maxLength: maxLength(100),
          required: requiredIf(function() {
            return this.airframeCompanyType == "6";
          })
        },
        bgaCompanyOther: {
          nameValidator,
          maxLength: maxLength(100),
          required: requiredIf(function() {
            return this.bgaCompanyType == "6";
          })
        },
        mroNeededBenefits: {
          required: requiredIf(function() {
            return [18].includes(parseInt(this.commercialCompanyType)) && this.commercialCompanyOther !== "";
          })
        }
      };
    } else {
      return {
        commercialProduct: {
          requiredProduct
        },
        airframeProduct: {
          requiredProduct
        },
        bgaProduct: {
          requiredProduct
        },
        company: {
          purchaseNewEngine: {
            required
          },
          companyName: {
            nameValidator,
            maxLength: maxLength(100),
            required
          },
          companyCountry: {
            required
          },
          companyAnotherName: {
            nameValidator,
            maxLength: maxLength(100)
          },
          prevCompanyLegalName: {
            nameValidator,
            maxLength: maxLength(100),
            required: requiredIf(function() {
              return !this.company.commercialLicenseRenewal && this.company.isLegalNameChange == "Yes";
            })
          },
          nameChangeDate: {
            required: requiredIf(function() {
              return !this.company.commercialLicenseRenewal && this.company.isLegalNameChange == "Yes";
            })
          },
          parentCompany: {
            nameValidator,
            maxLength: maxLength(100)
          },
          supportGEContact: {
            maxLength: maxLength(100),
            required: requiredIf(function() {
              return this.company.purchaseNewEngine;
            })
          },
          currentAgreementNumber: {
            required: requiredIf(function() {
              return this.company.commercialLicenseRenewal;
            })
          },
          dunsNumber: {
            maxLength: maxLength(100),
            required: requiredIf(function() {
              return this.commercialProduct && (this.commercialCompanyType == "1" || this.commercialCompanyType == "3") 
                    && this.company.dunsNumberAvailable == true;
            })
          },
          companyWebsiteLink: {
            maxLength: maxLength(200),
            required
          },
          contactEmail: {
            email,
            maxLength: maxLength(150),
            required: requiredIf(function() {
              return this.company.purchaseNewEngine;
            })
          },
          contactPhoneNumber: {
            maxLength: maxLength(15),
            required: requiredIf(function() {
              return this.company.purchaseNewEngine;
            })
          },
          invoiceReceipt: {
            required: requiredIf(function() {
              return this.commercialProduct && (this.commercialCompanyType == "1" || this.commercialCompanyType == "3");
            })
          },
          purchaseParts: {
            required: requiredIf(function() {
              return this.commercialProduct && (this.commercialCompanyType == "1" || this.commercialCompanyType == "3");
            })
          },
          purchaseTypeofParts: {
            required: requiredIf(function() {
              return this.company.purchaseParts === 'Yes' && this.commercialProduct
                    && (this.commercialCompanyType == "1" || this.commercialCompanyType == "3");
            })
          },
          purchasingContactName: {
            maxLength: maxLength(100),
            required: requiredIf(function() {
              return this.company.purchaseParts === 'Yes' && this.commercialProduct
                    && (this.commercialCompanyType == "1" || this.commercialCompanyType == "3");
            })
          },
          purchasingContactEmail: {
            email,
            maxLength: maxLength(150),
            required: requiredIf(function() {
              return this.company.purchaseParts === 'Yes' && this.commercialProduct
                    && (this.commercialCompanyType == "1" || this.commercialCompanyType == "3");
            })
          },
          purchaseSubmitOrder: {
            required: requiredIf(function() {
              return this.company.purchaseParts === 'Yes' && this.commercialProduct
                    && (this.commercialCompanyType == "1" || this.commercialCompanyType == "3");
            })
          }
        },
        commercialCompanyType: {
          required: requiredIf(function() {
            return this.commercialProduct;
          })
        },
        airframeCompanyType: {
          required: requiredIf(function() {
            return this.airframeProduct;
          })
        },
        bgaCompanyType: {
          required: requiredIf(function() {
            return this.bgaProduct;
          })
        },
        commercialCompanyOther: {
          required: requiredIf(function() {
            return [18].includes(parseInt(this.commercialCompanyType));
          })
        },
        airframeCompanyOther: {
          nameValidator,
          maxLength: maxLength(100),
          required: requiredIf(function() {
            return this.airframeCompanyType == "6";
          })
        },
        bgaCompanyOther: {
          nameValidator,
          maxLength: maxLength(100),
          required: requiredIf(function() {
            return this.bgaCompanyType == "6";
          })
        },
        legalNameChangeDocument: {
          required: requiredIf(function() {
            return (
              (this.company.commercialLicenseRenewal && this.company.companyNameChanged) ||
              (!this.company.commercialLicenseRenewal && this.company.isLegalNameChange == "Yes")
            );
          })
        },
        mroNeededBenefits: {
          required: requiredIf(function() {
            return [18].includes(parseInt(this.commercialCompanyType)) && this.commercialCompanyOther !== "";
          })
        },
        companyVerificationDocument: {
          required: requiredIf(function() {
            return (
              this.commercialProduct && 
              (this.commercialCompanyType == "1" || this.commercialCompanyType == "3") && this.company.dunsNumberAvailable == false
            );
          })
        }
      };
    }
  },
  created() {
    this.$store.dispatch("country/getCountries");
    this.$store.dispatch("productInfo/getProductInfo");
    this.$store.dispatch("carrierList/getCarrierList");
    if (this.company.nameChangeDate) {
      this.company.nameChangeDate = moment(this.company.nameChangeDate).format("YYYY-MM-DD");
    }
    if (this.$store.state.vuealidate.hasOwnProperty("General Company Information")) {
      this.$v.$touch();
    }
    //show TNC checkbox if Full Engine Overhaul is selected
    if (this.$store.state.product.productTypes.commercial !== null) {
      this.agreedOverhaulTNC = this.$store.state.product.productTypes.commercial.companyDetails.companyOtherName == "Full Engine Overhaul" ? true : false;
    }
  },
  beforeDestroy() {
    this.$v.$touch();
    this.$store.dispatch("vuealidate/saveValidation", {
      componentName: "General Company Information",
      $v: this.$v
    });
  },
  methods: {
    gotoNextSection(e) {
      e.preventDefault();
      if (this.validateFormData() === true) {
        this.formErrorMsg = true;
        setTimeout(
          function() {
            this.formErrorMsg = false;
          }.bind(this),
          5000
        );
      } else {
        this.formErrorMsg = false;
        this.$emit("next", "GeneralInfo");
      }
    },
    validateFormData() {
      let error = false;
      let inputs = [
        "commercialProduct",
        "commercialCompanyType",
        "airframeCompanyType",
        "bgaCompanyType",
        "commercialCompanyOther",
        "airframeCompanyOther",
        "bgaCompanyOther",
        "mroNeededBenefits"
      ];
      // check if user has selected purchase new engines or not
      this.$v.company.purchaseNewEngine.$touch();
      if(this.$v.company.purchaseNewEngine.$error) {
        return true;
      }
      // check all required fields are filled
      for (let i = 0; i < inputs.length; i++) {
        this.$v[inputs[i]].$touch();
        error = this.$v[inputs[i]].$error;
        if (error) break;
      }
      return error;
    },
    updateCompanyOther(productType, name) {
      this.$store.dispatch("product/updateCompanyOther", { productType, name });
    },
    addNewProduct(productName) {
      let product;
      let company = {
        companyTypeId: "",
        companyTypeDesc: "",
        companyOtherName: "",
        mroNeededBenifits: ""
      };
      switch (productName) {
        case "commercial": {
          product = {
            productTypeId: 1,
            productTypeDesc: "Commercial Engines",
            companyDetails: new Company(company),
            productInfo: null
          };
          break;
        }
        case "airframe": {
          product = {
            productTypeId: 2,
            productTypeDesc: "Airframe Systems",
            companyDetails: new Company(company),
            productInfo: null
          };
          break;
        }
        case "bga":
          {
            product = {
              productTypeId: 4,
              productTypeDesc: "Business & General Aviation Engines (BGA)",
              companyDetails: new Company(company),
              productInfo: null
            };
          }
          break;
      }
      this.$store.dispatch("product/setNewProduct", new Product(product));
      //default set 1st options once new product is selected
      switch (productName) {
        //if product is commercial set Airline as default, in case of renewal set Vendor as default
        case "commercial": {          
          if(this.company.commercialLicenseRenewal){
            this.commercialCompanyType = "11";
          }
          else{
            this.commercialCompanyType = "1";
          }
        }
          break;
        //if product is airframe set Airline as default
        case "airframe":
          this.airframeCompanyType = "1";
          break;
        //if product is bga set Owner/Operator as default
        case "bga":
          this.bgaCompanyType = "2";
          break;
      }
    },
    getCompanyType(productType, companyId) {
      let companyName;
      switch (productType) {
        case "commercial":
          companyName = this.commercial.companyType.find(x => x.companyTypeId == companyId).companyTypeName;
          break;
        case "airframe":
          companyName = this.airframe.companyType.find(x => x.companyTypeId == companyId).companyTypeName;
          break;
        case "bga":
          companyName = this.bga.companyType.find(x => x.companyTypeId == companyId).companyTypeName;
          break;
        default:
          companyName = "";
      }
      return companyName;
    },
    changeCompanyType(productType, companyId) {
      let product;
      let mro = {
        reqMroDetailsChanged: true,
        servicesMRO: "",
        modificationToRenewedAgreement: "",
        documents: []
      };
      let company = {
        companyTypeId: companyId,
        companyTypeDesc: companyId == "" ? "" : this.getCompanyType(productType, companyId)
      };
      switch (productType) {
        case "commercial": {
          product = {
            productType: "commercial",
            companyDetails: new Company(company),
            productInfo: null
          };
          if (companyId == 1 || companyId == 3 || companyId == 6) {
            product.productInfo = new ProductInfo();
          }
          break;
        }
        case "airframe": {
          product = {
            productType: "airframe",
            companyDetails: new Company(company),
            productInfo: null
          };
          product.productInfo = new ProductInfo();
          // delete unused property ‘aircraftType’
          delete product.productInfo.engineDetails[0].engineModel;
          break;
        }
        case "bga":
          {
            product = {
              productType: "bga",
              companyDetails: new Company(company),
              productInfo: new ProductInfo()
            };
          }
          break;
      }
      this.$store.dispatch("product/AddCompany", product);
      if(productType == "commercial") {
        if ([11, 12, 13, 14, 15, 16, 17, 18, 19].includes(parseInt(this.commercialCompanyType))) {
          this.$store.dispatch("mro/addNewMRO", new Mro(mro));
        } 
        else {
          this.$store.dispatch("mro/removeMRO");
        }
        //reset DUNSNumber section except for Commercial Engines -> Airline OR Lessor
        if(![1, 3].includes(parseInt(this.commercialCompanyType))) {
          this.company.dunsNumber = "";
          this.company.dunsNumberAvailable = true;
          this.$v.company.dunsNumber.$reset();
          this.company.invoiceReceipt = [];
          this.$v.company.invoiceReceipt.$reset();
          this.company.purchaseParts = null;
          this.$v.company.purchaseParts.$reset();
          this.company.purchaseTypeofParts = [];
          this.$v.company.purchaseTypeofParts.$reset();
          this.company.purchasingContactName = "";
          this.$v.company.purchasingContactName.$reset();
          this.company.purchasingContactEmail = "";
          this.$v.company.purchasingContactEmail.$reset();
          this.company.purchaseSubmitOrder = "";
          this.$v.company.purchaseSubmitOrder.$reset();
          this.$store.dispatch("files/removeFile", "CompanyVerificationDocument");
          this.$store.dispatch("company/removeFileMetaData", { documentType: "CompanyVerificationDocument" });
        }
      }
      //remove files in case of change event
      this.removeFile(productType);
    },
    removeFile(productType){
      let mroDocTypes = [
        "FAAPart145RepairStationCertificate",
        "CapabilitiesListDocument",
        "OperatorSpecification",
        "AirworthinessAuthorityDocumentation",
        "UpdatedContactsList",
        "DataProtectionPlanDocument",
        "CAMOCertification",
        "RepairServicesOrOtherAgreementWithGEDocument",
        "VerificationOfConsultancyDocument",
        "OtherDocument"
      ];
      let commercialProdDocTypes = [
        "CommercialFleetSummaryDocument",
        "CommercialOperatorCertificate",
        "CommercialOperatorSpecDocument",
        "LeaseAgreementCertificate",
        "BillOfSaleDocument",
        "AssetDocument",
        "AuthLetterDocument",
        "CommercialRelationshipDocument"
      ];
      let airframeProdDocType = ["LetterOfDelegation"];
      let bgaProdDocType = [
        "FleetSummaryDocument",
        "OperatorCertDocument",
        "BGACapabilitiesListDocument",
        "BGADataProtectionPlanDocument",
        "BGAFAAPart145RepairStationCertificate",
        "BGARatingLimitationDocument"
      ];
      let filetobeRemoved = []
      switch (productType) {
        case "commercial": {
          filetobeRemoved = commercialProdDocTypes.concat(mroDocTypes);
          break;
        }
        case "airframe": {
          filetobeRemoved = airframeProdDocType;
          break;
        }
        case "bga": {
          filetobeRemoved = bgaProdDocType;
          break;
        }          
      }
      filetobeRemoved.forEach(file => {
        this.$store.dispatch("files/removeFile", file);
      })
    },
    changeCommercialCompanyOther(){
      let mro = {
        reqMroDetailsChanged: true,
        servicesMRO: "",
        modificationToRenewedAgreement: "",
        documents: []
      };
      this.$store.dispatch("mro/addNewMRO", new Mro(mro));
      this.removeFile("commercial");
    },
    back() {
      this.$router.push("/");
    },
    //Show tooltip for Commercial Engines - Description of Entity options    
    tooltipCommercial(companyTypeId){
      //companyTypeId 1 for Airline and companyTypeId 3 for Lessor
      if(companyTypeId == "1" || companyTypeId == "3"){
        return "Application Suggestion: Short Form General Terms Agreement (SFGTA) for selected Engine Models";
      }
      //GE/CFM-Owned MRO (51% or more)
      else if(companyTypeId == "19"){
        return "Application Suggestion: MRO Services License Agreement";
      }
      //Consultant
      else if(companyTypeId == "13"){
        return "Application Suggestion: Consultant Agreement"
      }
      //Purchasing Agent
      else if(companyTypeId == "12"){
        return "Application Suggestion: Purchasing Agent Agreement"
      }
      //Vendor
      else if(companyTypeId == "11"){
        return "Application Suggestion: MRO Vendor Sourcing Licensing Agreement"
      }
      else{
        return ""
      }
    },
    //show tooltip at right or left based on width for Commercial Engines - Description of Entity options
    tooltipPlacement(index) {
      let windowWidth = window.innerWidth
      if(windowWidth >= 1200) {
        return index%4 == 3 ? 'left' : 'right'
      }
      else if(windowWidth >= 992) {
        return index%3 == 2 ? 'left' : 'right'
      }
      else if(windowWidth >= 768) {
        return index%2 == 1 ? 'left' : 'right'
      }
      else {
        return 'right'
      } 
    },
    updateMRONeededBenefits(productType, name) {
      this.$store.dispatch("product/updateMRONeededBenefits", { productType, name });
    },
    //Once FAA 145 Approved MRO or Equivalent is selected set default MRO Need Benefits
    setMRONeededBenefits(value) {
      if(value == "Line Maintenance") {
        this.mroNeededBenefits = "LMSA - Line Maintenance Service Agreement";
      }
      else if(value == "Full Engine Overhaul") {
        this.mroNeededBenefits = "PLA-OH - Purchase License Agreement - Overhaul";
      }
      else if(value == "Engine Piece Part or Engine Component Repair") {
        this.mroNeededBenefits = "PLA-CR - Purchase License Agreement - Component Repair";
      }
      else {
        this.mroNeededBenefits = "";
      }
    },
    //Call when action is taken on Modal TNC for Full Engine Overhaul
    overhaulTNCModalAction(value) {
      if(value == 'Cancel') {
        this.agreedOverhaulTNC = false;
        this.showOverhaulTNCModal = false;
        this.commercialCompanyOther = "";
      }
      if(value == 'Confirm') {
        this.agreedOverhaulTNC = true;
        this.showOverhaulTNCModal = false;
      }
    },
    getDocumentStatus(docType) {
      let status = "";
      let storedDocs = this.$store.state.company.company.companyDocuments;
      if (storedDocs != null && storedDocs.length != 0) {
        for (let i = 0; i < storedDocs.length; i++) {
          if (storedDocs[i].documentType == docType) {
            status = storedDocs[i].status;
            break;
          }
        }
      }
      return status;
    }
  }
};
</script>
<style lang="less" scoped>
#general-company-info {
  padding-bottom: 90px;
}
.form-control {
  max-width: 255px;
}
.action-btns {
  margin-top: 0;
}
#product-type-info {
  width: 200px;
}
#product-type {
  padding-left: 10px;
  label {
    font-weight: normal;
  }
}
#military-warning-message {
  margin-top: 20px;
  color: blue;
}
#legal-name {
  width: 70px;
}
.renewal-radio-btns {
  padding-left: 10px;
  .radio-btn {
    display: inline-block;
    margin-right: 20px;
    label {
      vertical-align: bottom;
      margin-left: 5px;
      font-weight: normal;
    }
  }
}
.aggrementnum {
  margin-top: 10px;
}
.mb-3 {
  margin-bottom: 15px;
}
.productType input[disabled] + span {
  color: gray;
}
.radio-inline input[type="radio"]{
  cursor: pointer;
}
.radio-padding-bottom {
  padding-bottom: 1px;
}
.row-padding-left {
  padding-left: 10px;
}
.mro-benefits-link {
  text-decoration: underline;
  font-weight:bold;
  cursor: pointer;
  margin-left: 10px;
}
.mro-benefits-padding {
  padding-left: 8px;
  padding-right: 0;
}
.mro-row-margin {
  margin: initial;
}
.mro-padding-right {
  padding-right: initial;
}
.lbloverhaulTNC {
  padding-top: 8px;
  cursor: default;
}
.margin-0 {
  margin: 0px;
}
.padding-left-0 {
  padding-left: 0px;
}
input[type="text"]:disabled {
  background: #dddddd !important;
}
.text-top {
  vertical-align: text-top;
}
.mt-0 {
  margin-top: 0px;
}
.mb-0 {
  margin-bottom: 0px;
}
@media only screen and (min-width: 768px) { 
  .lbloverhaulTNC {
    white-space: nowrap;
  }
}
</style>
